import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Group, Block, CapacityException } from '@/features/stm/lib/types'
import { toast } from 'sonner'

/** Payload for creating a group; contact_name/email/phone are derived from contactUserId on backend */
export type CreateGroupPayload = Omit<
  Group,
  'id' | 'created_at' | 'updated_at' | 'contact_name' | 'contact_email' | 'contact_phone'
> & { regionId?: number | null }

function parseStmErrorResponse(text: string, status: number): string {
  try {
    const json = JSON.parse(text) as { message?: string; error?: string }
    if (typeof json.message === 'string' && json.message) return json.message
    if (typeof json.error === 'string' && json.error) return json.error
  } catch {
    // ignore
  }
  return text || `HTTP ${status}`
}

import { authFetch } from '@/shared/lib/auth-fetch'
import { ADMIN } from '@/shared/constants/backend-api'

async function stmFetch(url: string, init?: RequestInit) {
  const res = await authFetch(url, init)
  if (!res.ok) {
    const text = await res.text()
    throw new Error(parseStmErrorResponse(text, res.status))
  }
  return res.json()
}

export function useStmCreateGroup(regionId: number | undefined | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (group: CreateGroupPayload) => {
      const rid = group.regionId ?? regionId
      if (rid == null) throw new Error('Region is required')
      return stmFetch(`/stm/groups?regionId=${rid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(group),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm', 'groups'] })
      qc.invalidateQueries({ queryKey: ['stm', 'calendar'] })
      toast.success('Group created')
    },
    onError: (e: Error) => toast.error('Failed to create group: ' + e.message),
  })
}

export type UpdateGroupVariables = Partial<Group> & {
  id: string
  /** When true, skip success/error toasts (e.g. PreTrip auto-mark for group leaders). Not sent to the API. */
  silent?: boolean
  /** When saving notes, tag these user IDs to send them a notification (stripped by backend before DB update). */
  taggedUserIds?: number[]
  /** Sender name for note-tag notifications (stripped by backend). */
  senderName?: string
  /** Path to the group page for the notification link (stripped by backend; stored in notification.actionPath). */
  actionPath?: string | null
}

export function useStmUpdateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, silent: _silent, ...updates }: UpdateGroupVariables) => {
      await stmFetch('/stm/groups', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates }),
      })
    },
    onSuccess: async (_data, variables) => {
      const updatedGroup = await stmFetch(
        `/stm/groups/${encodeURIComponent(variables.id)}`
      ) as Group

      qc.setQueriesData(
        { queryKey: ['stm', 'group', variables.id] },
        () => updatedGroup
      )

      qc.setQueriesData(
        { queryKey: ['stm', 'groups'] },
        (old: Group[] | undefined) => {
          if (!Array.isArray(old)) return old
          const next = old.map((g) =>
            String(g.id) === String(variables.id) ? updatedGroup : g
          )
          return next
        }
      )

      qc.setQueriesData(
        { queryKey: ['stm', 'calendar'] },
        (old: { weeks?: unknown[]; groups?: Array<Record<string, unknown>> } | undefined) => {
          if (!old?.groups) return old
          const next = {
            ...old,
            groups: old.groups.map((g) =>
              String(g.id) === String(variables.id)
                ? { ...g, ...updatedGroup }
                : g
            ),
          }
          return next
        }
      )

      qc.invalidateQueries({ queryKey: ['stm', 'groups'] })
      qc.invalidateQueries({ queryKey: ['stm', 'group', variables.id] })
      qc.invalidateQueries({ queryKey: ['stm', 'calendar'] })
      // Members / volunteers tab (remote leader visibility, stayingOnBase, counts)
      qc.invalidateQueries({ queryKey: [ADMIN.tableRows] })
      qc.invalidateQueries({ queryKey: [ADMIN.groupVolunteersBase, variables.id] })
      if (!variables.silent) {
        toast.success('Group updated')
      }
    },
    onError: (e: Error, variables) => {
      if (variables?.silent) return
      toast.error('Failed to update group: ' + e.message)
    },
  })
}

export function useStmDeleteGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await stmFetch(`/stm/groups?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm', 'groups'] })
      qc.invalidateQueries({ queryKey: ['stm', 'calendar'] })
      toast.success('Group deleted')
    },
    onError: (e: Error) => toast.error('Failed to delete group: ' + e.message),
  })
}

export function useStmCreateBlock(regionId: number | undefined | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (block: Omit<Block, 'id' | 'created_at' | 'updated_at'>) => {
      return stmFetch(`/stm/blocks?regionId=${regionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(block),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Block created')
    },
    onError: (e: Error) => toast.error('Failed to create block: ' + e.message),
  })
}

export function useStmUpdateBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Block> & { id: string }) => {
      await stmFetch('/stm/blocks', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Block updated')
    },
    onError: (e: Error) => toast.error('Failed to update block: ' + e.message),
  })
}

export function useStmDeleteBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await stmFetch(`/stm/blocks?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Block deleted')
    },
    onError: (e: Error) => toast.error('Failed to delete block: ' + e.message),
  })
}

// --- Capacity exceptions (all: blocks + overrides) ---

export function useStmCapacityExceptions(regionId: number | undefined | null) {
  return useQuery({
    queryKey: ['stm', 'capacity-exceptions', regionId ?? ''],
    queryFn: () =>
      stmFetch(`/stm/capacity-exceptions?regionId=${regionId}`) as Promise<CapacityException[]>,
    enabled: !!regionId,
  })
}

export function useStmCreateCapacityException(regionId: number | undefined | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      start_date: string
      end_date: string
      capacity: number
      reason?: string | null
      severity?: 'Hard Block' | 'Soft Block' | null
    }) => {
      return stmFetch(`/stm/capacity-exceptions?regionId=${regionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Capacity exception created')
    },
    onError: (e: Error) => toast.error('Failed to create capacity exception: ' + e.message),
  })
}

export function useStmUpdateCapacityException() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<CapacityException> & { id: string }) => {
      await stmFetch('/stm/capacity-exceptions', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Capacity exception updated')
    },
    onError: (e: Error) => toast.error('Failed to update capacity exception: ' + e.message),
  })
}

export function useStmDeleteCapacityException() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await stmFetch(`/stm/capacity-exceptions?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stm'] })
      toast.success('Capacity exception deleted')
    },
    onError: (e: Error) => toast.error('Failed to delete capacity exception: ' + e.message),
  })
}
